import { DatePicker, IComboBox, IComboBoxOption, IComboBoxStyles, IDatePickerStyleProps, IDatePickerStyles, IRenderFunction, IStyleFunctionOrObject, ITextFieldProps, ITextFieldStyleProps, ITextFieldStyles, TextField, Toggle, VirtualizedComboBox } from "@fluentui/react";
import { comboboxStyles, datePickerStyles, datePickertextFieldStyles, flagStyles, lgComboboxStyles, lgDatePickerStyles, lgDatePickerTextFieldStyles, lgFlagStyles, lgTextFieldStyles, mdComboboxStyles, mdDatePickerStyles, mdDatePickerTextFieldStyles, mdFlagStyles, mdTextFieldStyles, smComboboxStyles, smDatePickerStyles, smDatePickerTextFieldStyles, smFlagStyles, smTextFieldStyles, textFieldStyles, xlComboboxStyles, xlDatePickerStyles, xlDatePickerTextFieldStyles, xlFlagStyles, xlTextFieldStyles, xxlComboboxStyles, xxlDatePickerStyles, xxlDatePickerTextFieldStyles, xxlFlagStyles, xxlTextFieldStyles } from "./Styles/RegisterFieldStyles";
import { useBreakpoint } from "../../../Container/ContainerStyles";
import { BreakpointType } from "../../../Container/ContainerFluid";
import { SystemCore } from "../../../../../Core/System/SystemCore";

//#region TextField

export interface IRegisterTextFieldProps {
    placeholder: string;
    value: string;
    maxLength?: number;
    onlyNumbers?: boolean;
    suffix?: string;
    label?: string;
    description?: string;
    readOnly?: boolean;
    errorMessage?: string;
    autoFocus?: boolean;
    styles?: IStyleFunctionOrObject<ITextFieldStyleProps, ITextFieldStyles>;
    onChange: (value: string) => void;
    onRenderSuffix?: IRenderFunction<ITextFieldProps> | undefined; 
}

export const RegisterTextField: React.FC<IRegisterTextFieldProps> = (props) => {
    const isSM = useBreakpoint(BreakpointType.sm);
    const isMD = useBreakpoint(BreakpointType.md);
    const isLG = useBreakpoint(BreakpointType.lg);
    const isXL = useBreakpoint(BreakpointType.xl);
    const isXXL = useBreakpoint(BreakpointType.xxl);
    
    const handleChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        if (props.onlyNumbers) {
            const numericValue = newValue?.replace(/[^0-9]/g, "") || "";
            props.onChange(numericValue);
        } else {
            props.onChange(newValue || "");
        }
    };

    return (
        <TextField
            readOnly={props.readOnly}
            maxLength={props.maxLength}
            styles={
                props.styles != undefined ? 
                props.styles 
                : 
                    isSM ? 
                        smTextFieldStyles : 
                    isMD ? 
                        mdTextFieldStyles :
                    isLG ? 
                        lgTextFieldStyles :
                    isXL ?
                        xlTextFieldStyles :
                    isXXL ?
                        xxlTextFieldStyles
                    :
                        textFieldStyles
            }
            autoFocus={props.autoFocus}
            label={props.label}
            description={props.description}
            placeholder={props.placeholder}
            value={props.value}
            suffix={props.suffix}
            errorMessage={props.errorMessage}
            onChange={handleChange}
            onRenderSuffix={props.onRenderSuffix}
        />
    );
}

//#endregion TextField

//#region ComboBox

export interface IRegisterComboBoxProps {
    placeholder: string;
    value: string | number | string[] | number[] | null | undefined;
    default?: string | number | string[] | number[] | null | undefined;
    options: IComboBoxOption[];
    freeInput?: boolean | undefined;
    maxLength?: number;
    onlyNumbers?: boolean;
    suffix?: string;
    label?: string;
    readOnly?: boolean;
    errorMessage?: string;
    autoFocus?: boolean;
    styles?: Partial<IComboBoxStyles> | undefined;
    onChange: (event: React.FormEvent<IComboBox> | null, option?: IComboBoxOption | null, index?: number | null, value?: string) => void;
    onRenderSuffix?: IRenderFunction<ITextFieldProps> | undefined; 
}

export const RegisterComboBox: React.FC<IRegisterComboBoxProps> = (props) => {
    const isSM = useBreakpoint(BreakpointType.sm);
    const isMD = useBreakpoint(BreakpointType.md);
    const isLG = useBreakpoint(BreakpointType.lg);
    const isXL = useBreakpoint(BreakpointType.xl);
    const isXXL = useBreakpoint(BreakpointType.xxl);

    return (
        <>
        {
            props.freeInput ?
                <RegisterTextField 
                    placeholder={props.placeholder}
                    value={props.value as string}
                    onChange={(value) => props.onChange(null, null, null, value)}
                    maxLength={props.maxLength}
                    onlyNumbers={props.onlyNumbers}
                    suffix={props.suffix}
                    styles={props.styles}
                    label={props.label}
                    readOnly={props.readOnly}
                    autoFocus={props.autoFocus}
                    errorMessage={props.errorMessage}
                    onRenderSuffix={props.onRenderSuffix}
                />
            :    
                <VirtualizedComboBox 
                    placeholder={props.placeholder}
                    defaultSelectedKey={props.default}
                    selectedKey={props.value}
                    errorMessage={props.errorMessage}
                    
                    autoComplete="on"
                    styles={
                        props.styles != undefined ? 
                        props.styles 
                        : 
                            isSM ? 
                                smComboboxStyles : 
                            isMD ? 
                                mdComboboxStyles :
                            isLG ?
                                lgComboboxStyles :
                            isXL ?
                                xlComboboxStyles :
                            isXXL ?
                                xxlComboboxStyles
                            :
                                comboboxStyles
                    }
                    
                    options={props.options}
                    onChange={props.onChange}
                />
        }
        </>
    );
};

//#endregion ComboBox

//#region DatePicker

export interface IRegisterDatePickerProps {
    placeholder: string;
    value: Date | undefined;
    default?: string | number | string[] | number[] | null | undefined;
    freeForm?: boolean | undefined;
    freeInput?: boolean | undefined;
    autoFocus?: boolean;
    styles?: IStyleFunctionOrObject<IDatePickerStyleProps, IDatePickerStyles> | undefined;
    onChange: (date: Date | null | undefined) => void;
}

export const RegisterDatePicker: React.FC<IRegisterDatePickerProps> = (props) => {
    const isSM = useBreakpoint(BreakpointType.sm);
    const isMD = useBreakpoint(BreakpointType.md);
    const isLG = useBreakpoint(BreakpointType.lg);
    const isXL = useBreakpoint(BreakpointType.xl);
    const isXXL = useBreakpoint(BreakpointType.xxl);

    const formatDate = (dateStr?: string): Date | undefined => {
        if (dateStr != undefined)
        {
            let convert: Date = new Date(dateStr);
            if (convert.getFullYear() == 1)
                return undefined;
                
            return convert;
        }

        return undefined;
    }

    return (
        <DatePicker
            borderless
            styles={
                props.styles != undefined ? 
                props.styles 
                : 
                    isSM ? 
                        smDatePickerStyles : 
                    isMD ? 
                        mdDatePickerStyles :
                    isLG ?
                        lgDatePickerStyles :
                    isXL ?
                        xlDatePickerStyles :
                    isXXL ?
                        xxlDatePickerStyles
                    :
                        datePickerStyles
            }
            textField={{
                autoFocus: props.autoFocus,
                styles: 
                    isSM ? 
                        smDatePickerTextFieldStyles : 
                    isMD ? 
                        mdDatePickerTextFieldStyles :
                    isLG ?
                        lgDatePickerTextFieldStyles :
                    isXL ? 
                        xlDatePickerTextFieldStyles : 
                    isXXL ?
                        xxlDatePickerTextFieldStyles
                    : 
                        datePickertextFieldStyles
            }}
            value={formatDate(props.value?.toString())}
            placeholder={props.placeholder}
            formatDate={(date) => {
                return SystemCore.formatDate(date);
            }}
            onSelectDate={(date) => {
                date!.setHours(date!.getHours() + 3);
                props.onChange(date);
            }}
        />
    );
};

//#endregion DatePicker

//#region Flag

export interface IRegisterFlagProps {
    label: string;
    inlineLabel: boolean;
    checked: boolean;
    onText?: string;
    offText?: string;
    onChange: (ev?: React.MouseEvent<HTMLElement>, checked?: boolean) => void;
}

export const RegisterFlag: React.FC<IRegisterFlagProps> = (props) => {
    const isSM = useBreakpoint(BreakpointType.sm);
    const isMD = useBreakpoint(BreakpointType.md);
    const isLG = useBreakpoint(BreakpointType.lg);
    const isXL = useBreakpoint(BreakpointType.xl);
    const isXXL = useBreakpoint(BreakpointType.xxl);

    return(
        <Toggle
            styles={
                isSM ?
                    smFlagStyles :
                isMD ?
                    mdFlagStyles :
                isLG ? 
                    lgFlagStyles :
                isXL ?
                    xlFlagStyles :
                isXXL ?
                    xxlFlagStyles 
                :
                    flagStyles
            }
            checked={props.checked}
            onText={props.onText}
            offText={props.offText}
            label={props.label}
            inlineLabel={props.inlineLabel}
            onChange={props.onChange}
        />
    )
};

//#endregion Flag